import { Button } from 'components/ui/button';
import { ScrollArea } from 'components/ui/scroll-area';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PATH_ROUTES } from 'routes/path';
import useRiskCheckerStore from 'store/risk-checker';
import LifestyleRecommendations from './LifestyleRecommendations';
import Icon from 'components/icon/Icon';

import PrescriptionImage from 'assets/images/prescription.png';

function shadeColor(color: string, percent: number) {
  let num = parseInt(color.slice(1), 16),
    amt = Math.round(2.55 * percent),
    r = (num >> 16) + amt,
    g = ((num >> 8) & 0x00ff) + amt,
    b = (num & 0x0000ff) + amt;

  return (
    '#' +
    (
      0x1000000 +
      (r < 0 ? 0 : r > 255 ? 255 : r) * 0x10000 +
      (g < 0 ? 0 : g > 255 ? 255 : g) * 0x100 +
      (b < 0 ? 0 : b > 255 ? 255 : b)
    )
      .toString(16)
      .slice(1)
  );
}

export default function RiskDetails() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const engineName = useRiskCheckerStore((store) => store.engineName);
  const viewRiskDetails = useRiskCheckerStore((store) => store.viewRiskDetails);

  return (
    <div className='mx-4 mt-4 mb-8 lg:my-4'>
      <ScrollArea className='lg:h-[calc(100vh-7rem)]'>
        <Button
          variant='outline'
          className='h-8 px-4 md:px-6 text-gray-700 border-gray-700 rounded-full text-sm'
          onClick={() => navigate(PATH_ROUTES.RISK_ANALYSIS)}
        >
          {t('go_back')}
        </Button>

        <div className='lg:px-4 pt-4 space-y-4'>
          <div className='flex flex-row gap-4 items-center justify-between'>
            <h1 className='text-xl lg:text-2xl font-semibold'>
              {engineName === 'diabetes_risk' ? 'Diabetes' : 'Hypertension'}{' '}
            </h1>
            {viewRiskDetails?.score_info?.category_color ? (
              <div
                className='flex items-center justify-between w-52 md:w-60 rounded-full border px-2 md:px-4 py-1.5 text-sm font-semibold'
                style={{
                  borderColor: viewRiskDetails?.score_info?.category_color,
                  backgroundColor: shadeColor(
                    viewRiskDetails?.score_info?.category_color || '#fff',
                    70
                  ),
                }}
              >
                <div className='flex items-center gap-1'>
                  <Icon
                    size={20}
                    icon='check-circle'
                    color={viewRiskDetails?.score_info?.category_color}
                  />
                  <p>{viewRiskDetails?.score_info?.risk_level}</p>
                </div>

                <p>
                  {viewRiskDetails?.score_info?.probability ||
                    viewRiskDetails?.score_info?.pre_diabetes_probability}
                  % chances
                </p>
              </div>
            ) : null}
          </div>

          <div className='bg-[#F1F5F9] rounded-lg p-4 space-y-2'>
            <div className='flex items-center gap-3'>
              <div className='grid bg-white rounded-md size-8 place-content-center'>
                <Icon icon='report1' size={20} color='#1A68D8' />
              </div>
              <h2 className='text-lg md:text-xl font-semibold text-tertiary'>
                Result
              </h2>
            </div>

            <p className='font-medium text-[#656D76] text-sm md:text-base'>
              {viewRiskDetails?.report_info?.result?.body}
            </p>
          </div>

          <div className='grid lg:grid-cols-2 gap-4'>
            <div className='flex p-4 text-white rounded-lg bg-tertiary'>
              <div className='flex flex-col mr-4 space-y-2 md:space-y-0'>
                <h2 className='text-lg md:text-xl font-semibold'>
                  Small Steps, Big Impact
                </h2>
                <p className='flex items-center text-sm md:text-base flex-1 col-span-3 font-medium'>
                  By making small changes today, you can build a healthier and
                  more comfortable future. Act now for lasting benefits!
                </p>
              </div>
              <div className='shrink-0'>
                <img
                  src={PrescriptionImage}
                  alt=''
                  className='mt-6 md:mr-4 w-20 h-20 md:w-28 md:h-28'
                  style={{ transform: 'scaleX(-1)' }}
                />
              </div>
            </div>
            <div className='p-4 rounded-lg bg-[#F3F4F6] space-y-1'>
              <div className='flex items-center gap-3'>
                <div className='grid bg-white rounded-md size-8 place-content-center'>
                  <Icon icon='monitor' color='#1A68D8' className='size-4' />
                </div>
                <h2 className='text-lg md:text-xl font-semibold text-tertiary'>
                  Periodic Monitoring
                </h2>
              </div>
              <p className='text-sm md:text-base font-medium text-[#656D76]'>
                Use MedsiCheck daily to monitor your Blood Pressure, Hemoglobin
                A1C, and Stress Levels. This frequent monitoring will help
                ensure these metrics remain within healthy ranges. If readings
                are above 160/100mmHg consult your physician as soon as
                possible.
              </p>
              <div className='flex justify-end text-sm'>
                <Button className='px-6 h-9'>Measure Now</Button>
              </div>
            </div>
          </div>

          <div className='bg-[#F1F5F9] rounded-lg p-4 space-y-2'>
            <div className='flex items-center gap-3'>
              <div className='grid bg-white rounded-md size-8 place-content-center'>
                <Icon icon='cross' size={20} color='#1A68D8' />
              </div>
              <h2 className='text-lg md:text-xl font-semibold text-tertiary'>
                Medical Referral
              </h2>
            </div>
            {viewRiskDetails?.report_info?.medical_referral?.body ? (
              <p className='font-medium text-[#656D76] text-sm md:text-base'>
                {viewRiskDetails?.report_info?.medical_referral?.body}
              </p>
            ) : null}
            {viewRiskDetails?.report_info?.medical_referral?.list?.length ? (
              <ul className='space-y-3'>
                {viewRiskDetails?.report_info?.medical_referral?.list?.map(
                  (item) => (
                    <div className='flex items-center gap-2'>
                      <div className='grid rounded-sm bg-primary size-4 place-content-center'>
                        <Check className='text-white size-3' />
                      </div>
                      <li className='font-medium text-[#656D76] text-sm md:text-base'>
                        {item}
                      </li>
                    </div>
                  )
                )}
              </ul>
            ) : null}
          </div>

          <LifestyleRecommendations
            data={viewRiskDetails?.report_info?.lifestyle_recommendation}
          />
        </div>
      </ScrollArea>
    </div>
  );
}
